import { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Grid,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Alert,
    FormHelperText,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';

const RegistrationForm = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [formData, setFormData] = useState({
        ClientId: 0,
        FirstName: '',
        LastName: '',
        Gender: '',
        ClientCategoryID: 0,
        Organization: 'Total Turf Experience',
        Email: [''],
        PhoneCell: '',
        Address1: '',
        Address2: '',
        City: '',
        StateProv: '',
        PostalCode: '',
        Country: 'United States',
        EmailSubscriptions: [
            {
                EmailAddress: '',
                EmailVerificationStatus: 'Unverified',
                Subscriptions: [
                    {
                        Subscription: "CreditCardNotification",
                        OptIn: true
                    },
                    {
                        Subscription: "EmailCampaign",
                        OptIn: true
                    },
                    {
                        Subscription: "SessionReminders",
                        OptIn: true
                    }
                ]
            }
        ]
    });
    const [existingUsers, setExistingUsers] = useState([]);
    const [showExistingUsersModal, setShowExistingUsersModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [confirmError, setConfirmError] = useState('');

    const US_STATES = [
        'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
        'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
        'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
        'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
        'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
    ];

    const toTitleCase = (str) => {
        return str.toLowerCase().split(' ').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const validatePhoneNumber = (phone) => {
        const re = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
        return re.test(phone);
    };

    const formatPhoneNumber = (phone) => {
        const cleaned = phone.replace(/\D/g, '');
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return '(' + match[1] + ') ' + match[2] + '-' + match[3];
        }
        return phone;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const newValidationErrors = { ...validationErrors };
        delete newValidationErrors[name];

        if (name === 'Email') {
            const isValid = validateEmail(value);
            if (!isValid && value.length > 0) {
                newValidationErrors.Email = 'Please enter a valid email address';
            }
            setFormData(prev => ({
                ...prev,
                Email: [value],
                EmailSubscriptions: [
                    {
                        ...prev.EmailSubscriptions[0],
                        EmailAddress: value
                    }
                ]
            }));
        } else if (name === 'PhoneCell') {
            const formattedPhone = formatPhoneNumber(value);
            const isValid = validatePhoneNumber(formattedPhone);
            if (!isValid && value.length > 0) {
                newValidationErrors.PhoneCell = 'Please enter a valid phone number';
            }
            setFormData(prev => ({
                ...prev,
                [name]: formattedPhone
            }));
        } else if (name === 'FirstName' || name === 'LastName') {
            setFormData(prev => ({
                ...prev,
                [name]: toTitleCase(value)
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }

        setValidationErrors(newValidationErrors);
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.FirstName) errors.FirstName = 'First name is required';
        if (!formData.LastName) errors.LastName = 'Last name is required';
        if (!formData.Gender) errors.Gender = 'Please select a gender';
        if (!formData.Email[0] || !validateEmail(formData.Email[0])) {
            errors.Email = 'Please enter a valid email address';
        }
        if (process.env.REACT_APP_PHONE_NUMBER_REQUIRED === 'true' && (!formData.PhoneCell || !validatePhoneNumber(formData.PhoneCell))) {
            errors.PhoneCell = 'Please enter a valid phone number';
        }
        if (!dateOfBirth) {
            errors.DateOfBirth = 'Date of birth is required';
        } else {
            const age = DateTime.now().diff(DateTime.fromISO(dateOfBirth.toISO()), 'years').years;
            if (age < 13) {
                errors.DateOfBirth = 'You must be at least 13 years old to register';
            }
        }
        if (!formData.Address1) errors.Address1 = 'Address is required';
        if (!formData.City) errors.City = 'City is required';
        if (!formData.StateProv) errors.StateProv = 'State is required';
        if (!formData.PostalCode) errors.PostalCode = 'Postal code is required';

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!validateForm()) {
            setError('Please correct the errors in the form');
            return;
        }

        setLoading(true);

        try {
            // First, check for existing users
            const checkResponse = await api.post('/auth/check-existing', {
                phoneNumber: formData.PhoneCell
            });

            if (checkResponse.data.exists && checkResponse.data.users.length > 0) {
                setExistingUsers(checkResponse.data.users);
                setShowExistingUsersModal(true);
                setLoading(false);
                return;
            }

            // If no existing users, proceed with registration
            const registrationData = {
                ClientId: 0,
                FirstName: formData.FirstName,
                LastName: formData.LastName,
                Gender: formData.Gender,
                ClientCategoryID: 0,
                Organization: formData.Organization,
                DateOfBirth: dateOfBirth ? dateOfBirth.toISO() : null,
                Email: [formData.Email[0]],
                EmailSubscriptions: [
                    {
                        EmailAddress: formData.Email[0],
                        EmailVerificationStatus: 'Unverified',
                        Subscriptions: [
                            {
                                Subscription: "CreditCardNotification",
                                OptIn: true
                            },
                            {
                                Subscription: "EmailCampaign",
                                OptIn: true
                            },
                            {
                                Subscription: "SessionReminders",
                                OptIn: true
                            }
                        ]
                    }
                ],
                Address1: formData.Address1,
                Address2: formData.Address2 || '',
                City: formData.City,
                Country: formData.Country,
                PostalCode: formData.PostalCode,
                StateProv: formData.StateProv,
                PhoneCell: formData.PhoneCell
            };

            const response = await api.post('/auth/register', registrationData);
            
            if (response.data.success) {
                navigate('/login', { 
                    state: { 
                        message: 'Registration successful! Please check your email to finish setting up your account. It may take up to 5 minutes for your account to be activated.' 
                    } 
                });
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleExistingUserSelect = (user) => {
        setSelectedUser(user);
        setShowConfirmationDialog(true);
    };

    const handleConfirmUser = async () => {
        setConfirmLoading(true);
        setConfirmError('');
        try {
            // Enable self-service for the selected user
            const response = await api.post('/auth/activate-self-service', {
                clientId: selectedUser.id
            });

            if (response.data.success) {
                setShowConfirmationDialog(false);
                setShowExistingUsersModal(false);
                navigate('/login', {
                    state: {
                        message: response.data.message || 'Account activation successful! Please check your email to finish setting up your account.'
                    }
                });
            }
        } catch (error) {
            const errorCode = error.response?.headers?.['ez-error-code'];
            const errorMessage = error.response?.headers?.['ez-error-message'];
            
            if (errorCode === 'ClientIsAlreadyEnabled') {
                setShowConfirmationDialog(false);
                setShowExistingUsersModal(false);
                navigate('/login', {
                    state: {
                        message: 'Your account is already enabled. You can log in with your credentials.'
                    }
                });
            } else {
                setConfirmError(errorMessage || error.response?.data?.message || 'Failed to activate account. Please try again.');
            }
        } finally {
            setConfirmLoading(false);
        }
    };

    const handleConfirmNo = async () => {
        setShowConfirmationDialog(false);
        setShowExistingUsersModal(false);
        
        // Proceed with registration
        setLoading(true);
        try {
            const registrationData = {
                ClientId: 0,
                FirstName: formData.FirstName,
                LastName: formData.LastName,
                Gender: formData.Gender,
                ClientCategoryID: 0,
                Organization: formData.Organization,
                DateOfBirth: dateOfBirth ? dateOfBirth.toISO() : null,
                Email: [formData.Email[0]],
                EmailSubscriptions: [
                    {
                        EmailAddress: formData.Email[0],
                        EmailVerificationStatus: 'Unverified',
                        Subscriptions: [
                            {
                                Subscription: "CreditCardNotification",
                                OptIn: true
                            },
                            {
                                Subscription: "EmailCampaign",
                                OptIn: true
                            },
                            {
                                Subscription: "SessionReminders",
                                OptIn: true
                            }
                        ]
                    }
                ],
                Address1: formData.Address1,
                Address2: formData.Address2 || '',
                City: formData.City,
                Country: formData.Country,
                PostalCode: formData.PostalCode,
                StateProv: formData.StateProv,
                PhoneCell: formData.PhoneCell
            };

            const response = await api.post('/auth/register', registrationData);
            
            if (response.data.success) {
                navigate('/login', { 
                    state: { 
                        message: 'Registration successful! Please check your email to finish setting up your account.' 
                    } 
                });
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSuccessDialogClose = () => {
        setSuccessDialogOpen(false);
        navigate('/login');
    };

    return (
        <>
            <Box component="form" onSubmit={handleSubmit} noValidate>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            fullWidth
                            label="First Name"
                            name="FirstName"
                            value={formData.FirstName}
                            onChange={handleChange}
                            error={!!validationErrors.FirstName}
                            helperText={validationErrors.FirstName}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            fullWidth
                            label="Last Name"
                            name="LastName"
                            value={formData.LastName}
                            onChange={handleChange}
                            error={!!validationErrors.LastName}
                            helperText={validationErrors.LastName}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            required
                            fullWidth
                            label="Email"
                            name="Email"
                            type="email"
                            value={formData.Email[0]}
                            onChange={handleChange}
                            error={!!validationErrors.Email}
                            helperText={validationErrors.Email}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            required={process.env.REACT_APP_PHONE_NUMBER_REQUIRED === 'true'}
                            fullWidth
                            label="Phone Number"
                            name="PhoneCell"
                            value={formData.PhoneCell}
                            onChange={handleChange}
                            error={!!validationErrors.PhoneCell}
                            helperText={validationErrors.PhoneCell || 'Format: (123) 456-7890'}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl fullWidth required error={!!validationErrors.Gender}>
                            <InputLabel>Gender</InputLabel>
                            <Select
                                name="Gender"
                                value={formData.Gender}
                                onChange={handleChange}
                                label="Gender"
                            >
                                <MenuItem value="Male">Male</MenuItem>
                                <MenuItem value="Female">Female</MenuItem>
                                <MenuItem value="Undefined">Prefer not to say</MenuItem>
                            </Select>
                            {validationErrors.Gender && (
                                <FormHelperText>{validationErrors.Gender}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <LocalizationProvider dateAdapter={AdapterLuxon}>
                            <DatePicker
                                label="Date of Birth"
                                value={dateOfBirth}
                                onChange={setDateOfBirth}
                                maxDate={DateTime.now()}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        required: true,
                                        error: !!validationErrors.DateOfBirth,
                                        helperText: validationErrors.DateOfBirth
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            required
                            fullWidth
                            label="Address"
                            name="Address1"
                            value={formData.Address1}
                            onChange={handleChange}
                            error={!!validationErrors.Address1}
                            helperText={validationErrors.Address1}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Address Line 2"
                            name="Address2"
                            value={formData.Address2}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            fullWidth
                            label="City"
                            name="City"
                            value={formData.City}
                            onChange={handleChange}
                            error={!!validationErrors.City}
                            helperText={validationErrors.City}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControl
                            required
                            fullWidth
                            error={!!validationErrors.StateProv}
                        >
                            <InputLabel>State</InputLabel>
                            <Select
                                name="StateProv"
                                value={formData.StateProv}
                                onChange={handleChange}
                                label="State"
                            >
                                {US_STATES.map((state) => (
                                    <MenuItem key={state} value={state}>
                                        {state}
                                    </MenuItem>
                                ))}
                            </Select>
                            {validationErrors.StateProv && (
                                <FormHelperText>{validationErrors.StateProv}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            fullWidth
                            label="Postal Code"
                            name="PostalCode"
                            value={formData.PostalCode}
                            onChange={handleChange}
                            error={!!validationErrors.PostalCode}
                            helperText={validationErrors.PostalCode}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            fullWidth
                            label="Country"
                            name="Country"
                            value={formData.Country}
                            onChange={handleChange}
                        />
                    </Grid>
                </Grid>

                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{ mt: 3, mb: 2, py: 1.5 }}
                >
                    {loading ? <CircularProgress size={24} /> : 'Register'}
                </Button>

                <Button
                    fullWidth
                    variant="text"
                    onClick={() => navigate('/login')}
                    sx={{ textTransform: 'none' }}
                >
                    Already have an account? Log in
                </Button>
            </Box>

            <Dialog
                open={successDialogOpen}
                onClose={handleSuccessDialogClose}
                aria-labelledby="registration-success-dialog"
            >
                <DialogTitle id="registration-success-dialog">
                    Registration Successful
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Your registration was successful. Please check your email for an invitation to create your online account. 
                        Once you finish setting up your account, you can log in on the next page.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSuccessDialogClose} color="primary" variant="contained">
                        Proceed to Login
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showExistingUsersModal}
                onClose={() => setShowExistingUsersModal(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Existing Accounts Found</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        We found existing accounts with this phone number. Please select your account from the list below:
                    </DialogContentText>
                    <Box sx={{ width: '100%' }}>
                        {existingUsers.map((user) => (
                            <Button
                                key={user.id}
                                onClick={() => handleExistingUserSelect(user)}
                                variant="outlined"
                                fullWidth
                                sx={{ 
                                    mb: 1,
                                    justifyContent: 'flex-start',
                                    textAlign: 'left',
                                    p: 2
                                }}
                            >
                                <Box>
                                    <Typography variant="subtitle1">
                                        {user.firstName} {user.lastName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Email: {user.email || 'Not provided'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Phone: {user.phoneNumber || 'Not provided'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Gender: {user.gender || 'Not specified'}
                                    </Typography>
                                </Box>
                            </Button>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowExistingUsersModal(false)}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmNo}
                        disabled={loading}
                        variant="contained"
                    >
                        Create New Account
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showConfirmationDialog}
                onClose={() => !confirmLoading && setShowConfirmationDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Confirm Account</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Is this your account?
                        <Box sx={{ mt: 2, mb: 1 }}>
                            <Typography variant="subtitle1">
                                {selectedUser?.firstName} {selectedUser?.lastName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Email: {selectedUser?.email || 'Not provided'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Phone: {selectedUser?.phoneNumber || 'Not provided'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Gender: {selectedUser?.gender || 'Not specified'}
                            </Typography>
                        </Box>
                        {confirmError && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {confirmError}
                            </Alert>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setShowConfirmationDialog(false)}
                        disabled={confirmLoading}
                    >
                        No
                    </Button>
                    <Button 
                        onClick={handleConfirmUser} 
                        variant="contained"
                        disabled={confirmLoading}
                        startIcon={confirmLoading ? <CircularProgress size={20} /> : null}
                    >
                        {confirmLoading ? 'Activating...' : 'Yes, this is me'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default RegistrationForm; 